import Link from 'next/link'
import { useRouter } from 'next/router'
import clsx from 'clsx'
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/20/solid'
import {
  Square3Stack3DIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline'
import React, { useState, useEffect, FC } from 'react'

import { isSamePath } from '@/utils/comparePaths'

interface SubLink {
  title: string
  href: string
  newTab?: boolean
}

interface NavigationLink extends SubLink {
  links?: SubLink[]
}

interface NavigationSection {
  title: string
  id?: string
  links: NavigationLink[]
}

export const navigation: NavigationSection[] = [
  {
    title: 'Setup Guide',
    id: 'setup-guide',
    links: [
      {
        title: 'Welcome to SELECT',
        href: '/docs',
      },
      {
        title: 'Step 1: Create Snowflake User',
        href: '/docs/snowflake-metadata-access',
      },
      {
        title: 'Step 2: Connect Snowflake Account',
        href: '/docs/connect-snowflake-account',
      },
    ],
  },
  {
    title: 'Using SELECT',
    links: [
      {
        title: 'Workloads',
        href: '/docs/workloads',
      },
      {
        title: 'Lineage',
        href: '/docs/lineage',
      },
      {
        title: 'Insights',
        href: '/docs/insights',
      },
      {
        title: 'Usage Groups',
        href: '/docs/usage-groups',
      },
      {
        title: 'Budgets',
        href: '/docs/budgets',
      },
      {
        title: 'Monitors',
        href: '/docs/monitors',
        links: [
          {
            title: 'Overview',
            href: '/docs/monitors/overview',
          },
          {
            title: 'Configure Destinations',
            href: '/docs/monitors/destinations',
          },
          {
            title: 'Using Monitors',
            href: '/docs/monitors/usage',
          },
          {
            title: 'Custom SQL Monitors',
            href: '/docs/monitors/custom-sql',
          },
        ],
      },
      {
        title: 'Organization Overview',
        href: '/docs/snowflake-organization',
      },
      {
        title: 'Accessing Data in SELECT',
        href: '/docs/accessing-select-data',
      },
      {
        title: 'Views',
        href: '/docs/views',
      },
      {
        title: 'Managing Access',
        href: '/docs/manage-access',
        links: [
          {
            title: 'User Management',
            href: '/docs/manage-access/user-management',
          },
          {
            title: 'Role Based Access Control',
            href: '/docs/manage-access/role-based-access-control',
          },
          {
            title: 'Teams',
            href: '/docs/manage-access/teams',
          },
          {
            title: 'Okta SSO',
            href: '/docs/manage-access/okta-sso',
          },
          {
            title: 'Azure AD SSO',
            href: '/docs/manage-access/azure-ad-sso',
          },
        ],
      },
    ],
  },
  {
    title: 'Integrations',
    links: [
      {
        title: 'dbt',
        href: '/docs/dbt',
      },
      {
        title: 'Custom Workloads',
        href: '/docs/custom-workloads',
      },
      {
        title: 'Looker',
        href: '/docs/looker',
      },
      {
        title: 'Mode',
        href: '/docs/mode',
      },
      {
        title: 'Hex',
        href: '/docs/hex',
      },
      {
        title: 'Periscope',
        href: '/docs/periscope',
      },
      {
        title: 'Fivetran',
        href: '/docs/fivetran',
      },
      {
        title: 'Opsgenie',
        href: '/docs/opsgenie',
      },
    ],
  },
  {
    title: 'Snowflake Developer Guide',
    id: 'snowflake-developer-guide',
    links: [
      {
        title: 'Resources',
        href: '/docs/snowflake-developer-guide',
      },
    ],
  },
  {
    title: 'Health Check Native App',
    id: 'health-check-native-app',
    links: [
      {
        title: 'About',
        href: '/docs/health-check-app',
      },
    ],
  },
  {
    title: 'Comparisons',
    links: [
      {
        title: 'Frequency Asked Questions',
        href: '/docs/comparisons/faq',
      },
      {
        title: 'SELECT vs. Homegrown',
        href: '/docs/select-vs-homegrown',
      },
      {
        title: 'SELECT vs. Snowflake',
        href: '/docs/select-vs-snowflake',
      },
      {
        title: 'SELECT vs. Keebo',
        href: '/docs/select-vs-keebo',
      },
      // {
      //   title: 'SELECT vs. Espresso AI',
      //   href: '/docs/select-vs-espresso-ai',
      // },
      {
        title: 'SELECT vs. Slingshot',
        href: '/docs/select-vs-slingshot',
      },
      {
        title: 'SELECT vs. Chaos Genius',
        href: '/docs/select-vs-chaos-genius',
      },
    ],
  },
  {
    title: 'dbt-snowflake-monitoring',
    id: 'dbt-snowflake-monitoring',
    links: [
      {
        title: 'About',
        href: '/docs/dbt-snowflake-monitoring',
      },
      {
        title: 'Quickstart',
        href: '/docs/dbt-snowflake-monitoring/quickstart',
      },
      {
        title: 'Example Usage',
        href: '/docs/dbt-snowflake-monitoring/example-usage',
      },
      {
        title: 'Contributing',
        href: '/docs/dbt-snowflake-monitoring/contributing',
      },
    ],
  },
  {
    title: 'API Reference',
    id: 'api-reference',
    links: [
      {
        title: 'Overview',
        href: 'https://api-docs.select.dev/api-reference/introduction',
        newTab: true,
      },
    ],
  },
]

interface SectionProps {
  section: NavigationSection
}

interface LinkItemProps {
  href: string
  title: string
  padding: string
  isActive: boolean
  newTab?: boolean
}

function Section({ section }: SectionProps) {
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)

  const toggleOpen = () => {
    setIsOpen(!isOpen)
  }

  // Check if the current path matches any link in the section
  // Automatically open / close accordingly
  useEffect(() => {
    const matchFound = section.links.some(
      (link) =>
        router.pathname === link.href ||
        (link.links &&
          link.links.some((sublink) => router.pathname === sublink.href))
    )
    setIsOpen(matchFound)
  }, [router.pathname, section.links])

  const LinkItem: FC<LinkItemProps> = ({
    href,
    title,
    padding,
    isActive,
    newTab,
  }) => (
    <Link key={title} href={href} target={newTab ? '_blank' : '_self'}>
      <div
        className={clsx(
          `ml-[0.7rem] border-l-2`,
          padding,
          isActive ? 'border-sky-500' : 'border-slate-800'
        )}
      >
        <div className="rounded-md px-2 py-[0.375rem] text-slate-400 hover:bg-slate-800">
          {title}
        </div>
      </div>
    </Link>
  )

  return (
    <div>
      <div
        className={clsx(
          'font-display flex items-center gap-x-3 rounded-md px-1 py-2 text-sm font-normal text-white hover:cursor-pointer hover:bg-slate-800',
          isOpen ? 'mb-1' : ''
        )}
        onClick={toggleOpen}
      >
        {!isOpen && <ChevronRightIcon className="h-4 w-4" />}
        {isOpen && <ChevronDownIcon className="h-4 w-4" />}
        {section.title}
      </div>

      {isOpen && (
        <div className="flex flex-col text-sm">
          {section.links.map((subsection) => (
            <React.Fragment key={subsection.title}>
              <LinkItem
                href={subsection.href}
                title={subsection.title}
                padding={'pl-4'}
                isActive={isSamePath(subsection.href, router.pathname)}
                newTab={subsection.newTab}
              />
              {subsection.links &&
                subsection.links.map((subsubsection) => (
                  <LinkItem
                    key={subsubsection.title}
                    href={subsubsection.href}
                    title={subsubsection.title}
                    padding={'pl-8'}
                    isActive={isSamePath(subsubsection.href, router.pathname)}
                    newTab={subsubsection.newTab}
                  />
                ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

export function DocsNavigation(): JSX.Element {
  const referenceSections = navigation.filter((section) =>
    ['Setup Guide', 'Using SELECT', 'Integrations', 'API Reference'].includes(
      section.title
    )
  )

  const resourcesSections = navigation.filter((section) =>
    [
      'Snowflake Developer Guide',
      'Health Check Native App',
      'Comparisons',
      'dbt-snowflake-monitoring',
    ].includes(section.title)
  )

  return (
    <nav className="w-60 pr-4 xl:w-64 xl:pr-6">
      {/* <SearchButton /> */}
      <div className="flex flex-col">
        <div className="flex items-center gap-x-2 py-2 text-sm font-bold text-white">
          <Square3Stack3DIcon className="h-6 w-6" />
          REFERENCE
        </div>
        {referenceSections.map((section) => (
          <Section key={section.title} section={section} />
        ))}
      </div>
      <div className="flex flex-col gap-y-0 pt-12">
        <div className="0ext-sm flex items-center gap-x-1 py-2 text-sm font-bold text-white">
          <AcademicCapIcon className="h-6 w-6" />
          RESOURCES
        </div>
        {resourcesSections.map((section) => (
          <Section key={section.title} section={section} />
        ))}
      </div>
    </nav>
  )
}
